import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { BarChart2, Trophy, Calendar, Share2, Lock } from 'lucide-react';

const AcademySection = () => {
  const [activeTab, setActiveTab] = useState('foundation');

  const modules = {
    foundation: [
      { number: 1, title: "Why You're Broke (and it's not your fault)", duration: '4 min', badge: 'Free', locked: false },
      { number: 2, title: 'The Psychology of Spending', duration: '5 min', badge: 'Free', locked: false },
      { number: 3, title: 'Budget Basics That Actually Work', duration: '4 min', badge: 'Free', locked: false },
      { number: 4, title: 'Emergency Fund — Your Financial Foundation', duration: '3 min', badge: 'Pro', locked: true },
      { number: 5, title: 'Debt — Understanding the Beast', duration: '5 min', badge: 'Pro', locked: true },
    ],
    growth: [
      { number: 6, title: 'The Snowball vs Avalanche Methods', duration: '4 min', badge: 'Pro', locked: true },
      { number: 7, title: 'Credit Score Mastery', duration: '5 min', badge: 'Pro', locked: true },
      { number: 8, title: 'Side Hustle Finance', duration: '4 min', badge: 'Pro', locked: true },
      { number: 9, title: 'Automating Your Finances', duration: '3 min', badge: 'Pro', locked: true },
      { number: 10, title: 'The Art of Negotiation', duration: '5 min', badge: 'Pro', locked: true },
    ],
    mastery: [
      { number: 11, title: 'Introduction to Investing', duration: '5 min', badge: 'Premium', locked: true },
      { number: 12, title: 'Retirement Planning Basics', duration: '4 min', badge: 'Premium', locked: true },
      { number: 13, title: 'Tax Optimization Strategies', duration: '5 min', badge: 'Premium', locked: true },
      { number: 14, title: 'Real Estate Fundamentals', duration: '4 min', badge: 'Premium', locked: true },
      { number: 15, title: 'Building Generational Wealth', duration: '5 min', badge: 'Premium', locked: true },
    ],
  };

  const featurePills = [
    { icon: BarChart2, text: 'Financial IQ Score' },
    { icon: Trophy, text: 'Badges & Achievements' },
    { icon: Calendar, text: 'Weekly Challenges' },
    { icon: Share2, text: 'Shareable Milestones' },
  ];

  const getBadgeStyles = (badge) => {
    switch (badge) {
      case 'Free':
        return 'bg-[#1B4332] text-white';
      case 'Pro':
        return 'bg-[#D4AF37] text-[#1B4332]';
      case 'Premium':
        return 'bg-gradient-to-r from-[#D4AF37] to-[#F39C12] text-[#1B4332]';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  };

  return (
    <section
      id="academy"
      data-testid="academy-section"
      className="bg-[#FAFAF7] section-padding"
    >
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p
            data-testid="academy-section-label"
            className="font-body text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#95B8A2] mb-4"
          >
            Findell Academy
          </p>
          <h2
            data-testid="academy-section-headline"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2D2D] leading-tight mb-4"
          >
            The Financial Education You
            <br />
            Never Got in School.
          </h2>
          <p className="font-body text-lg text-[#2D2D2D]/70 max-w-2xl mx-auto">
            3 tracks. 30 lessons. Delivered conversationally by Vera — triggered by your real financial behavior.
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="foundation" className="w-full max-w-4xl mx-auto" onValueChange={setActiveTab}>
          <TabsList className="w-full flex justify-center bg-transparent gap-2 mb-8">
            {['foundation', 'growth', 'mastery'].map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                data-testid={`academy-tab-${tab}`}
                className={`px-6 py-3 rounded-full font-body font-medium text-sm capitalize transition-all duration-300 ${
                  activeTab === tab
                    ? 'bg-[#1B4332] text-white shadow-lg'
                    : 'bg-white text-[#2D2D2D] border border-[#1B4332]/10 hover:bg-[#1B4332]/5'
                }`}
              >
                {tab === 'foundation' ? 'Foundation (Free)' : tab === 'growth' ? 'Growth (Pro)' : 'Mastery (Premium)'}
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.entries(modules).map(([key, moduleList]) => (
            <TabsContent key={key} value={key} className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {moduleList.map((module, index) => (
                  <div
                    key={module.number}
                    data-testid={`module-card-${module.number}`}
                    className={`card p-5 animate-fade-in-up relative ${module.locked ? 'overflow-hidden' : ''}`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    {/* Lock Overlay for Pro/Premium modules */}
                    {module.locked && (
                      <div className="absolute inset-0 bg-[#2D2D2D]/60 backdrop-blur-[2px] flex flex-col items-center justify-center z-10 rounded-2xl">
                        <Lock className="w-6 h-6 text-white mb-2" />
                        <span className="font-body text-xs text-white font-semibold">Upgrade to unlock</span>
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-3">
                      <span className="font-mono text-2xl font-bold text-[#1B4332]/20">
                        {String(module.number).padStart(2, '0')}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-body font-semibold ${getBadgeStyles(module.badge)}`}>
                        {module.locked && <Lock className="w-3 h-3 inline mr-1" />}
                        {module.badge}
                      </span>
                    </div>
                    <h4 className="font-heading text-base font-bold text-[#2D2D2D] mb-2 leading-snug">
                      {module.title}
                    </h4>
                    <span className="font-body text-xs text-[#2D2D2D]/50">
                      {module.duration}
                    </span>
                  </div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Vera Conversation Mockup */}
        <div className="max-w-lg mx-auto mt-16">
          <div
            data-testid="vera-conversation-mockup"
            className="bg-white rounded-3xl shadow-xl overflow-hidden border border-[#1B4332]/5"
          >
            {/* Phone header */}
            <div className="bg-[#1B4332] py-4 px-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-[#95B8A2] rounded-full flex items-center justify-center text-lg">
                🌿
              </div>
              <div>
                <p className="font-body text-sm font-semibold text-white">Vera</p>
                <p className="font-body text-xs text-white/60">Your Financial Coach</p>
              </div>
            </div>
            
            {/* Message */}
            <div className="p-6">
              <div className="bg-[#FAFAF7] rounded-2xl rounded-tl-none p-4 mb-4">
                <p className="font-body text-sm text-[#2D2D2D] leading-relaxed">
                  Good morning! Based on your spending last week you spent <span className="font-semibold text-[#E74C3C]">$340 on dining out</span> — 23% over your goal. Ready for a 3-minute lesson on the psychology of impulse spending?
                </p>
              </div>
              
              <div className="flex gap-3">
                <div
                  data-testid="lesson-start-button"
                  className="flex-1 bg-[#1B4332] text-white py-3 text-sm text-center rounded-full font-body font-semibold select-none"
                >
                  Start Lesson
                </div>
                <div
                  data-testid="lesson-later-button"
                  className="flex-1 bg-transparent border-2 border-[#1B4332] text-[#1B4332] py-3 text-sm text-center rounded-full font-body font-semibold select-none"
                >
                  Maybe Later
                </div>
              </div>
            </div>
          </div>
          
          <p className="font-body text-sm text-[#2D2D2D]/50 text-center mt-4 italic">
            Lessons triggered by your real behavior — not a random curriculum.
          </p>
        </div>

        {/* Feature Pills */}
        <div className="flex flex-wrap justify-center gap-3 mt-12">
          {featurePills.map((pill) => (
            <span
              key={pill.text}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm font-body text-[#2D2D2D] border border-[#1B4332]/10"
            >
              <pill.icon className="w-4 h-4 text-[#1B4332]" />
              {pill.text}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AcademySection;
